import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import {
  MarketingHeader, MarketingFooter, Primary, Secondary,
} from "../components/layout/MarketingShell";

/* Only Free is priced, and only Free lists limits, because Free is the only
   plan the product can actually deliver today. The backend has no billing,
   no subscriptions, no trial logic and no per-plan message enforcement, so
   publishing prices and quotas for the paid tiers would be selling
   something that cannot yet be bought. The old page also advertised
   WhatsApp Business API integration, which does not exist anywhere in the
   codebase; it is not repeated here. */

const FREE_INCLUDES = [
  "1 chatbot",
  "Trained on your own website, documents and questions",
  "Answers in Bahasa Melayu and English",
  "Web chat widget for your site",
  "Contacts captured from conversations",
  "Bring your own AI key — OpenRouter, OpenAI, Anthropic, Google and more",
];

/* Named because they are genuinely planned, deliberately without numbers. */
const UPCOMING = [
  { name: "Starter", who: "Solo founders and small shops" },
  { name: "Business", who: "Growing teams with more than one assistant" },
  { name: "Pro", who: "Agencies and multi-location brands" },
];

const SERVICES = [
  {
    name: "Done-for-you setup",
    desc: "We collect your FAQs, products and tone of voice, train the assistant in Bahasa Melayu and English, and test it before it goes live.",
  },
  {
    name: "Managed service",
    desc: "You never touch a dashboard. Message our team to update your menu, products or promotions, and we review answer quality with you.",
  },
  {
    name: "Agency and white-label",
    desc: "Run Bicarra for your own clients, with your branding and a workspace per client.",
  },
];

export default function Pricing() {
  return (
    <div className="b2 min-h-screen bg-b2-canvas" data-testid="pricing-page">
      <MarketingHeader />

      <section className="max-w-[1120px] mx-auto px-6 pt-14 pb-12 text-center">
        <p className="text-[12px] font-semibold tracking-[0.18em] uppercase text-b2-indigo">
          Pricing
        </p>
        <h1 className="mt-5 text-[40px] sm:text-[52px] leading-[1.08] font-bold tracking-[-0.035em] text-b2-ink">
          Start free.<br />Pay when it earns its keep.
        </h1>
        <p className="mt-6 mx-auto max-w-[560px] text-[17px] leading-relaxed text-b2-muted">
          Bring your own AI key and the model cost stays between you and your provider.
        </p>
      </section>

      {/* ------------------------------- free -------------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pb-16">
        <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr] items-start">
          <div className="rounded-[20px] bg-b2-surface border border-b2-line p-8 sm:p-10">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-[22px] font-bold tracking-[-0.02em] text-b2-ink">Free</h2>
                <p className="mt-1 text-[14px] text-b2-muted">Everything you need to see if it works for you.</p>
              </div>
              <p className="text-[38px] font-bold tracking-[-0.03em] text-b2-ink leading-none">RM 0</p>
            </div>

            <ul className="mt-8 space-y-3.5">
              {FREE_INCLUDES.map((f) => (
                <li key={f} className="flex gap-3 text-[14.5px] text-b2-inksoft">
                  <Check className="w-[18px] h-[18px] text-b2-indigo shrink-0 mt-0.5" strokeWidth={2.2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9">
              <Primary to="/register" testid="pricing-start-free">Start free</Primary>
            </div>
            <p className="mt-4 text-[13px] text-b2-faint">No card needed. No coding needed.</p>
          </div>

          {/* ---------------------------- upcoming --------------------------- */}
          <div className="rounded-[20px] bg-b2-wash p-8 sm:p-10">
            <h2 className="text-[22px] font-bold tracking-[-0.02em] text-b2-ink">Paid plans</h2>
            <p className="mt-1.5 text-[14px] text-b2-inksoft">
              Being finalised. We would rather tell you the real price once than
              change it on you later. Every paid plan can also connect a model you
              run yourself (Ollama, LM Studio or vLLM).
            </p>

            <ul className="mt-7 space-y-4">
              {UPCOMING.map((p) => (
                <li key={p.name} className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[15px] font-semibold text-b2-ink">{p.name}</p>
                    <p className="text-[13px] text-b2-muted">{p.who}</p>
                  </div>
                  <span className="shrink-0 h-7 px-2.5 rounded-full bg-white/80 text-b2-washink text-[12px] font-medium inline-flex items-center">
                    Coming soon
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <Secondary to="/contact-us" testid="pricing-talk-to-us">Talk to us</Secondary>
            </div>
            <p className="mt-4 text-[13px] text-b2-washink/70">
              Tell us what you need and we will quote you directly in the meantime.
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------ services ----------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pb-20">
        <h2 className="text-[26px] font-bold tracking-[-0.03em] text-b2-ink">
          Rather we did it for you?
        </h2>
        <p className="mt-2 text-[15px] text-b2-muted max-w-[560px]">
          These are quoted per project, once we understand your business.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.name} className="rounded-[14px] bg-b2-surface border border-b2-line p-6">
              <h3 className="text-[16px] font-bold tracking-[-0.02em] text-b2-ink">{s.name}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-b2-muted">{s.desc}</p>
              <Link to="/contact-us"
                    className="mt-4 inline-flex items-center gap-1 text-[13.5px] font-medium text-b2-indigo hover:underline">
                Ask for a quote <ArrowRight className="w-4 h-4" strokeWidth={1.9} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------ cta band ----------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pb-20">
        <div className="rounded-[20px] bg-b2-wash px-8 py-14 text-center">
          <h2 className="text-[28px] sm:text-[32px] font-bold tracking-[-0.03em] text-b2-ink">
            Try it on your own business first.
          </h2>
          <p className="mt-3 text-[15.5px] text-b2-inksoft">
            The free plan is enough to see whether it answers your customers well.
          </p>
          <div className="mt-8">
            <Primary to="/register" testid="pricing-cta-start">Start free</Primary>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
